import React, { useState, useEffect } from 'react';
import { Save, FileText, ChevronRight, Check, Loader2, Type, AlignLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '@/App';

// Define which sections are long text (textarea) vs short text (input)
const LONG_TEXT_KEYS = ['content', 'hero_subtitle', 'testfahrt_subtitle', 'whyus_subtitle', 'faq_subtitle', 'cta_subtitle', 'success_text', 'features_subtitle', 'testimonials_subtitle', 'contact_subtitle', 'hero_badge'];

const isLongText = (key) => {
  return LONG_TEXT_KEYS.includes(key) || key.includes('content') || key.includes('desc') || key.includes('subtitle') || key.includes('text');
};

// Human-readable labels for section keys
const sectionLabels = {
  hero_title: 'Hero Überschrift',
  hero_subtitle: 'Hero Untertitel',
  hero_badge: 'Hero Badge',
  features_badge: 'Vorteile Badge',
  features_title: 'Vorteile Überschrift',
  features_subtitle: 'Vorteile Untertitel',
  feature_1_title: 'Vorteil 1 - Titel',
  feature_1_desc: 'Vorteil 1 - Beschreibung',
  feature_2_title: 'Vorteil 2 - Titel',
  feature_2_desc: 'Vorteil 2 - Beschreibung',
  feature_3_title: 'Vorteil 3 - Titel',
  feature_3_desc: 'Vorteil 3 - Beschreibung',
  feature_4_title: 'Vorteil 4 - Titel',
  feature_4_desc: 'Vorteil 4 - Beschreibung',
  whyus_badge: '"Warum wir" Badge',
  whyus_title: '"Warum wir" Überschrift',
  whyus_subtitle: '"Warum wir" Untertitel',
  whyus_1_title: 'Warum wir 1 - Titel',
  whyus_1_desc: 'Warum wir 1 - Beschreibung',
  whyus_2_title: 'Warum wir 2 - Titel',
  whyus_2_desc: 'Warum wir 2 - Beschreibung',
  whyus_3_title: 'Warum wir 3 - Titel',
  whyus_3_desc: 'Warum wir 3 - Beschreibung',
  whyus_stat: 'Statistik Zahl',
  whyus_stat_label: 'Statistik Label',
  testfahrt_badge: 'Testfahrt Badge',
  testfahrt_title: 'Testfahrt Überschrift',
  testfahrt_subtitle: 'Testfahrt Beschreibung',
  testimonials_title: 'Bewertungen Überschrift',
  testimonials_subtitle: 'Bewertungen Untertitel',
  blog_badge: 'Blog Badge',
  blog_title: 'Blog Überschrift',
  faq_title: 'FAQ Überschrift',
  faq_subtitle: 'FAQ Untertitel',
  cta_badge: 'CTA Badge',
  cta_title: 'CTA Überschrift',
  cta_subtitle: 'CTA Untertitel',
  form_title: 'Formular Überschrift',
  success_title: 'Erfolg Überschrift',
  success_text: 'Erfolg Text',
  sidebar_kontakt_title: 'Sidebar Kontakt-Überschrift',
  sidebar_telefon: 'Telefonnummer',
  sidebar_email: 'E-Mail Adresse',
  sidebar_standort_title: 'Sidebar Standort-Überschrift',
  sidebar_standort_name: 'Standort Name',
  sidebar_standort_adresse: 'Standort Adresse (HTML)',
  sidebar_oeffnungszeiten: 'Öffnungszeiten (HTML)',
  content: 'Seiteninhalt (HTML)',
  title: 'Seitentitel',
  stand: 'Stand-Datum',
  contact_title: 'Kontakt Überschrift',
  contact_subtitle: 'Kontakt Untertitel',
  faq_1_question: 'FAQ 1 - Frage',
  faq_1_answer: 'FAQ 1 - Antwort',
  faq_2_question: 'FAQ 2 - Frage',
  faq_2_answer: 'FAQ 2 - Antwort',
  faq_3_question: 'FAQ 3 - Frage',
  faq_3_answer: 'FAQ 3 - Antwort',
  faq_4_question: 'FAQ 4 - Frage',
  faq_4_answer: 'FAQ 4 - Antwort',
  faq_5_question: 'FAQ 5 - Frage',
  faq_5_answer: 'FAQ 5 - Antwort',
  faq_6_question: 'FAQ 6 - Frage',
  faq_6_answer: 'FAQ 6 - Antwort',
  faq_contact_text: 'FAQ Kontakt-Text',
  testfahrt_feature_1_title: 'Testfahrt Feature 1 - Titel',
  testfahrt_feature_1_desc: 'Testfahrt Feature 1 - Beschreibung',
  testfahrt_feature_2_title: 'Testfahrt Feature 2 - Titel',
  testfahrt_feature_2_desc: 'Testfahrt Feature 2 - Beschreibung',
  testfahrt_feature_3_title: 'Testfahrt Feature 3 - Titel',
  testfahrt_feature_3_desc: 'Testfahrt Feature 3 - Beschreibung',
  testfahrt_badge_text: 'Testfahrt Badge Text',
  testfahrt_badge_sub: 'Testfahrt Badge Untertitel',
  testfahrt_gps_title: 'GPS-Tracking Titel',
  testfahrt_gps_sub: 'GPS-Tracking Untertitel',
  cta_button_primary: 'CTA Button Text',
};

const getLabel = (key) => sectionLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const AdminContent = () => {
  const { admin } = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/page-contents`);
      setPages(res.data);
      if (res.data.length > 0 && !selectedPage) {
        selectPage(res.data[0]);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectPage = (page) => {
    setSelectedPage(page);
    setSections({ ...page.sections });
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/page-content/${selectedPage.page_key}`,
        { sections },
        { headers: { Authorization: `Bearer ${admin.token}` } }
      );
      toast.success('Inhalte gespeichert!');
      // Update local state
      setPages(prev => prev.map(p => 
        p.page_key === selectedPage.page_key 
          ? { ...p, sections } 
          : p
      ));
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API}/admin/seed-page-contents`,
        {},
        { headers: { Authorization: `Bearer ${admin.token}` } }
      );
      if (res.data.status === 'seeded') {
        toast.success(`${res.data.count} Seiten angelegt!`);
        fetchPages();
      } else {
        toast.info('Inhalte bereits vorhanden');
      }
    } catch (error) {
      toast.error('Fehler beim Anlegen');
    } finally {
      setSeeding(false);
    }
  };

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const addSection = () => {
    const key = prompt('Neuen Abschnitt-Key eingeben (z.B. "neue_ueberschrift"):');
    if (key && key.trim()) {
      setSections(prev => ({ ...prev, [key.trim()]: '' }));
    }
  };

  const deleteSection = (key) => {
    if (window.confirm(`Abschnitt "${getLabel(key)}" wirklich löschen?`)) {
      setSections(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-content-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Seiteninhalte</h1>
          <p className="text-slate-500 mt-1">Texte aller Seiten bearbeiten</p>
        </div>
        {pages.length === 0 && (
          <Button onClick={handleSeed} disabled={seeding} data-testid="seed-content-btn">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            Standard-Inhalte anlegen
          </Button>
        )}
      </div>

      {pages.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Noch keine Seiteninhalte</h3>
            <p className="text-slate-500 mb-6">Klicke auf "Standard-Inhalte anlegen" um die editierbaren Texte für alle Seiten zu erstellen.</p>
            <Button onClick={handleSeed} disabled={seeding}>
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Standard-Inhalte anlegen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Page List Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-2">
                <div className="space-y-1">
                  {pages.map(page => (
                    <button
                      key={page.page_key}
                      onClick={() => selectPage(page)}
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between transition-colors ${
                        selectedPage?.page_key === page.page_key
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      data-testid={`page-select-${page.page_key}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{page.page_label || page.page_key}</span>
                      </div>
                      {selectedPage?.page_key === page.page_key && (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Editor */}
          <div className="lg:col-span-3">
            {selectedPage ? (
              <Card>
                <div className="border-b px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      {selectedPage.page_label || selectedPage.page_key}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {Object.keys(sections).length} Textabschnitte
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={addSection} data-testid="add-section-btn">
                      + Abschnitt
                    </Button>
                    <Button 
                      onClick={handleSave} 
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="save-content-btn"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Speichern
                    </Button>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {Object.entries(sections).map(([key, value]) => (
                      <div key={key} className="group" data-testid={`section-${key}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            {isLongText(key) ? (
                              <AlignLeft className="w-3.5 h-3.5 text-slate-400" />
                            ) : (
                              <Type className="w-3.5 h-3.5 text-slate-400" />
                            )}
                            {getLabel(key)}
                          </Label>
                          <button
                            onClick={() => deleteSection(key)}
                            className="text-xs text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Entfernen
                          </button>
                        </div>
                        {key === 'content' ? (
                          <Textarea
                            value={value}
                            onChange={(e) => updateSection(key, e.target.value)}
                            rows={15}
                            className="font-mono text-sm"
                            placeholder="HTML-Inhalt..."
                          />
                        ) : isLongText(key) ? (
                          <Textarea
                            value={value}
                            onChange={(e) => updateSection(key, e.target.value)}
                            rows={3}
                            placeholder={`${getLabel(key)} eingeben...`}
                          />
                        ) : (
                          <Input
                            value={value}
                            onChange={(e) => updateSection(key, e.target.value)}
                            placeholder={`${getLabel(key)} eingeben...`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  Wähle eine Seite aus der Liste
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContent;
